import { BankOutlined, CheckOutlined } from '@ant-design/icons';
import {
  AdminPortal,
  ContextHolder,
  useAuth,
  useAuthActions,
  useFeatureEntitlements,
  useTenantsState,
} from '@frontegg/react';
import {
  Dropdown as AntDropdown,
  AutoComplete,
  Divider,
  Menu,
  Breadcrumb,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BackIconMobile,
  DropdownIcon,
  EditIcon,
  SearchIcon,
  SignoutIcon,
  ToggleIcon,
} from 'src/icons';
import { getFromStorage } from 'src/lib/storage';
import { StripeBillingCustomerPortal } from 'src/modules/ambient_solo/components/Payments/StripeCustomerPortal';
import {
  encounterListSlice,
  resetEncounterState,
  setMobileRecord,
} from 'src/modules/ambient_solo/store/slices/encounter';
import {
  getAllPractitionersNamesSlice,
  getAppointmentByFilterSlice,
  getAppointmentsByStatusSlice,
  getAppointmentUuidSlice,
  getConditionByAppointmentTypeSlice,
  getPractitionerPreferencesSlice,
  getPractitionersAndPatientsSlice,
} from 'src/store/slices/appointments.slice';
import { getSettingsNotificationsSlice } from 'src/store/slices/settings.slice';
import { useNavigationStore } from 'src/hooks/navigation';
import { Text } from '../baseComponents';
import { ProfileTopNavbar } from '../baseComponents/ProfileTopNavbar';
import { analytics, RUM } from 'src/services';
import { useIsOP } from 'src/hooks/misc';
import {
  resetSummaryState,
  summaryListSlice,
} from 'src/modules/ambient_solo/store/slices/summary';
import { DemoControls } from 'src/pages/layout/demoControls';

export const TopNavbar = ({ handleToggleSidebar, isMinimized }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isOP = useIsOP();
  let { tenants } = useTenantsState();
  const { switchTenant } = useAuthActions();

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { isMobileRecord } = useSelector((state) => state?.encounterState);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const { isEntitled: isPaymentsFeatureEntitled } =
    useFeatureEntitlements('payments');

  const mobile_encounter_id = getFromStorage(
    `${currentPractitioner?.org_uuid}_topbar_encounter_id`
  );
  const switchOrganization = (e) => {
    localStorage.removeItem('filter_practitioner_id');
    localStorage.removeItem('selectedPractitioners');
    localStorage.removeItem('show_current_practitioner');
    localStorage.clear();

    // Clear important Redux state slices
    dispatch(getPractitionersAndPatientsSlice([]));
    dispatch(getAppointmentByFilterSlice([]));
    dispatch(getAppointmentsByStatusSlice([]));
    dispatch(getConditionByAppointmentTypeSlice(null));
    dispatch(getPractitionerPreferencesSlice(null));
    dispatch(getSettingsNotificationsSlice(null));
    dispatch(encounterListSlice([]));
    dispatch(summaryListSlice([]));
    dispatch(getAllPractitionersNamesSlice([]));
    dispatch(getAppointmentUuidSlice({}));
    dispatch(resetEncounterState());
    dispatch(resetSummaryState());

    const selectedId = e.key;
    switchTenant({ tenantId: selectedId });
  };

  const [pageTitle, setPageTitle] = useState('');

  const { title, breadcrumb } = useNavigationStore();

  const { user } = useAuth();

  if (user?.roles?.map((role) => role?.key)?.includes('provider')) {
    tenants = tenants.filter(
      (tenant) => tenant?.tenantId === currentPractitioner?.org_uuid
    );
  }

  const cachedUserName = getFromStorage(`user_name`);
  const org_id = currentPractitioner.org_id;
  const uuid = getFromStorage('uuid');

  const settings = () => {
    setDropdownVisible(false);
    // eslint-disable-next-line no-undef
    analytics.track('Viewed Settings', {
      email: user?.email,
      orgId: org_id,
    });

    // Clear browser cache before opening AdminPortal
    if ('caches' in window) {
      caches
        .keys()
        .then((cacheNames) => {
          return Promise.all(
            cacheNames.map((cacheName) => {
              return caches.delete(cacheName);
            })
          );
        })
        .then(() => {
          console.log('Browser cache cleared successfully');
          AdminPortal.show();
        })
        .catch((error) => {
          console.error('Error clearing cache:', error);
          AdminPortal.show();
        });
    } else {
      // Fallback if cache API is not available
      AdminPortal.show();
    }
  };

  const logout = () => {
    setDropdownVisible(false);
    const baseUrl = ContextHolder.getContext().baseUrl;
    // eslint-disable-next-line no-undef
    analytics.track('Signed Out', {
      email: user?.email,
      orgId: org_id,
    });
    RUM.clearUser();
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('show_current_practitioner');
    localStorage.clear();
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${
      isOP ? `${baseUrl}/oauth/account/login?organization=op` : window.location
    }`;
  };

  useEffect(() => {
    if (location.pathname !== '/settings') {
      dispatch(getPractitionerPreferencesSlice(null));
      dispatch(getConditionByAppointmentTypeSlice(null));
      dispatch(getSettingsNotificationsSlice(null));
    }
  }, [dispatch, location]);

  useEffect(() => {
    let titleObject = {
      '/appointments': 'Appointments',
      '/current-encounters': 'Current Encounter',
      '/encounters': 'Encounters',
      '/encountersbin': 'Aura Bin',
      [`/mobileEncounterDetails/${mobile_encounter_id}`]: 'Encounters',
      '/encounter': 'Aura AI',
      '/settings': 'Settings',
      '/superadmin': 'Super admin',
      '/csvupload': 'CSV Upload',
      '/superadmin/officepracticum': 'Office Practicum Settings',
      '/op-settings': 'Office Practicum Settings',
      '/open-dr-settings': 'OpenDr Settings',
      // '/humanscribe': 'Human Scribe',
      '/memory': 'Memory',
      '/templates': 'Templates',
      '/prompts': 'Prompts',
      '/ai': 'AI Configurations',
      '/formconditions': 'Form Conditions',
      '/autotemplate': 'Auto Template',
      '/frontdesk/calls': 'Calls',
      '/agents': 'Agents',
    };
    setPageTitle(`${titleObject[location?.pathname] || ''} `);
  }, [location, uuid, isMobileRecord, mobile_encounter_id]);

  let fullName = currentPractitioner.practitioner_name;
  let nameWithoutTitle = fullName.replace('Dr ', '');

  const isMobileView = window.innerWidth < 1260;
  const appointmentUUID = getFromStorage(
    `${currentPractitioner?.org_uuid}_appointmentUUID`
  );

  const isEncountersPage = () => {
    return (
      (location.pathname !== '/appointments' &&
        location.pathname !== '/settings' &&
        location.pathname !== `/schedule/${appointmentUUID}`) ||
      location.pathname === `/mobileEncounterDetails/${mobile_encounter_id}`
    );
  };

  const nullSelectedRecord = () => {
    if (location.pathname === `/schedule/${appointmentUUID}`) {
      history.back();
    } else if (location.pathname === `/settings`) {
      navigate(`/appointments`);
    } else {
      navigate(`/encounters`);
    }
    dispatch(setMobileRecord(false));
  };

  const [filteredTenantsNames, setFilteredTenantsNames] = useState(tenants);
  const [searchText, setSearchText] = useState('');

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = tenants.filter((tenant) =>
      tenant.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredTenantsNames(filtered);
  };

  const resetDropdown = () => {
    setDropdownVisible(false);
    setSearchText('');
    setFilteredTenantsNames(tenants);
  };

  // useEffect(() => {
  //   if (location.pathname === '/templatesexchange') {
  //     setIsLoading(true);
  //     dispatch(getAllTemplatesExchangeListAction()).finally(() =>
  //       setIsLoading(false)
  //     );
  //   }
  // }, [dispatch, location.pathname]);

  /**
   * Top Nav CTA scenarios
   * -------------------------------------------
   * Non Pro PLG user => View demo |
   * Non Pro PLG user with expired trial => Upgrade
   * Pro trial user => View demo |
   * Top tier Pro user => Book a demo |
   */

  const singleOrganisationAccessAccount =
    !searchText && filteredTenantsNames.length <= 1;
  // Hide organization switcher for single accounts
  const topNavbarDropdownOptions = [
    {
      label: (
        <div className="flex p-4">
          <EditIcon className={'mr-2'} />
          <span className="font-sans text-sm"> Settings</span>
        </div>
      ),
      key: 'settings',
      onClick: settings,
    },
    {
      label: (
        <>
          <Divider className="m-0 p-0" />
        </>
      ),
      className: 'p-0',
    },
    !singleOrganisationAccessAccount && {
      label: (
        <div className="flex p-4">
          <div className="text-sm">
            <BankOutlined className="mr-2" />
            Organizations
          </div>
        </div>
      ),
    },
    !singleOrganisationAccessAccount && {
      label: (
        <div className="p-2">
          <div className=" w-[19rem] ant-select-selector drop-shadow-md bg-white rounded-xl">
            <AutoComplete
              bordered={false}
              className="border-none font-sans h-10 flex items-center border-0 focus:outline-nones"
              style={{ WebkitTextFillColor: '#000' }}
              placeholder="Search organization"
              suffixIcon={<SearchIcon />}
              onSearch={handleSearch}
              onFocus={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={searchText}
              id="Searchorganization"
            />
          </div>
        </div>
      ),
    },
    !singleOrganisationAccessAccount && {
      label: (
        <Menu
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'white',
          }}
          id="menuorganization"
          items={filteredTenantsNames.map((tenant) => ({
            label: (
              <div
                key={tenant.tenantId}
                className="flex items-center p-2 text-sm text-gray-600"
              >
                {tenant.name}
                {tenant.tenantId === user.tenantId && (
                  <CheckOutlined className="ml-4 w-3 h-3" />
                )}
              </div>
            ),
            key: tenant.tenantId,
            disabled: tenant.tenantId === user.tenantId,
            onClick: switchOrganization,
          }))}
        />
      ),
    },
    !singleOrganisationAccessAccount &&
      currentPractitioner?.stripe_customer_id && {
        label: <Divider className="m-0 p-0" />,
      },
    currentPractitioner?.stripe_customer_id &&
      isPaymentsFeatureEntitled && {
        key: 'billing',
        label: <StripeBillingCustomerPortal />,
      },
    currentPractitioner?.stripe_customer_id && {
      label: <Divider className="m-0 p-0" />,
    },
    {
      label: (
        <div className="flex p-4">
          <SignoutIcon className={'mr-2'} />
          <span className="font-sans text-sm">Sign out</span>
        </div>
      ),
      key: 'logout',
      onClick: logout,
    },
  ].filter(Boolean);

  const renderPageHeader = () => {
    let titleJsx = null;
    let breadcrumbJsx = null;

    if (title) {
      if (typeof title === 'string') {
        titleJsx = <div className="font-semibold text-lg">{title}</div>;
      } else {
        titleJsx = title;
      }
    }

    if (breadcrumb?.length) {
      breadcrumbJsx = (
        <Breadcrumb
          items={breadcrumb.map(({ onClick, href, icon, title }) => ({
            title: (
              <div
                className={[
                  'flex items-center gap-1 h-[22px]',
                  (onClick || href) &&
                    'cursor-pointer text-primary hover:underline',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={(e) => {
                  onClick?.(e);
                  if (href) {
                    navigate(href);
                  }
                }}
              >
                {icon}
                {title}
              </div>
            ),
          }))}
        />
      );
    }

    if (pageTitle && !titleJsx && !breadcrumbJsx) {
      if (typeof pageTitle === 'string') {
        titleJsx = <div className="font-semibold text-lg">{pageTitle}</div>;
      } else {
        titleJsx = pageTitle;
      }
    }
    return titleJsx || breadcrumbJsx ? (
      <div className="flex flex-col">
        {breadcrumbJsx}
        {titleJsx}
      </div>
    ) : null;
  };

  return (
    <nav
      className={`top-navbar sticky top-0 left-0 z-[98] flex justify-between items-center ${
        isMinimized ? 'top-navbar--minimized' : ''
      }`}
    >
      <div className="top-navbar-left flex items-center justify-start">
        {!isMobileView && (
          <ToggleIcon
            className={'xl:hidden mr-3 sm:mr-4'}
            onClick={handleToggleSidebar}
          />
        )}
        {(isMobileView && isMobileRecord && isEncountersPage()) ||
        (isMobileView &&
          (location.pathname === '/settings' ||
            location.pathname === '/setting')) ||
        location.pathname === `/schedule/${appointmentUUID}` ? (
          <div className="xs:pr-2 md:pr-5 pr-5">
            <BackIconMobile onClick={nullSelectedRecord} />
          </div>
        ) : (
          ''
        )}
        {renderPageHeader()}
      </div>
      <div
        className={`flex justify-end items-center ${
          isMobileView ? 'gap-2' : 'gap-5'
        }`}
      >
        <DemoControls />
        <div className="top-navbar-right flex items-center">
          <AntDropdown
            className="profile-dropdown"
            overlayClassName="profile-dropdown-overlay"
            open={dropdownVisible}
            onClick={() => setDropdownVisible(true)}
            onOpenChange={() => {
              resetDropdown();
            }}
            menu={{
              onClick: (e) => {
                if (e.domEvent.stopImmediatePropagation) {
                  e.domEvent.stopImmediatePropagation();
                }
                if (e.domEvent.stopPropagation) {
                  e.domEvent.stopPropagation();
                }
                if (e.domEvent.preventDefault) {
                  e.domEvent.preventDefault();
                }
              },
              items: topNavbarDropdownOptions,
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div
              className="ant-dropdown-link"
              onClick={(e) => e.preventDefault()}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="profile-wrapper flex items-center cursor-pointer gap-1 sm:gap-2">
                <ProfileTopNavbar
                  name={nameWithoutTitle}
                  className={'profile-popover-logo'}
                />
                <Text type={'span'} className="user-name hidden sm:block">
                  {cachedUserName}
                  {tenants.length > 1 && (
                    <div className="text-xs text-right text-gray-500 flex items-center gap-1">
                      <BankOutlined />
                      {
                        tenants.find(
                          (tenant) => tenant.tenantId === user.tenantId
                        )?.name
                      }
                    </div>
                  )}
                </Text>
                <DropdownIcon />
              </div>
            </div>
          </AntDropdown>
        </div>
      </div>
    </nav>
  );
};
